/**
 * Boot writes from QuoteProvider.
 *
 * `upsert` and `calcule` both POST to the quotation API. `calcule` is
 * `POST /v1/quotations` (a price preview, it does not persist). It runs from
 * `fetchQuote` with no user edit, which is what Sentry shows as
 * `sentryFetch` in `sentryApiWrapper.ts`.
 */

/** Signed, and every application status that comes after signature. */
const LOCKED_APPLICATION_STATUSES = new Set([
  'signed',
  'paid',
  'pending evaluation',
  'quality control',
  'pending requirement',
  'approved',
  'rejected',
]);

export function isQuotationLocked(status: string | null | undefined): boolean {
  if (!status || !status.trim()) return false;
  return LOCKED_APPLICATION_STATUSES.has(status.trim().toLowerCase());
}

/**
 * Whether QuoteProvider may POST on this quote.
 * `desconocido` is a failed status read (5xx, network, timeout): do not write.
 * A missing application row is `editable`.
 */
export type QuoteWriteMode = 'desconocido' | 'bloqueado' | 'editable';

export function quoteWriteModeFor(input: {
  readFailed: boolean;
  status?: string | null;
}): QuoteWriteMode {
  if (input.readFailed) return 'desconocido';
  if (isQuotationLocked(input.status)) return 'bloqueado';
  return 'editable';
}

/** Boot `upsert` runs only once the status read says the quote is still editable. */
export function shouldSkipBootUpsert(mode: QuoteWriteMode): boolean {
  return mode !== 'editable';
}

function applicationPath(pathname: string): string {
  const path = pathname.split('?')[0].replace(/\/+$/, '');
  return path || '/';
}

/** `/application` and its children (`/application/summary`), not `/quote`. */
export function isApplicationPath(pathname: string): boolean {
  const path = applicationPath(pathname);
  return path === '/application' || path.startsWith('/application/');
}

/** `/quote` and `/quote/` (the Anterior link keeps the trailing slash). */
export function isQuotePath(pathname: string): boolean {
  return applicationPath(pathname) === '/quote';
}

/**
 * Skip the automatic `calcule` fired while loading a quote.
 *
 * A signed (or later) application must not be repriced. Opening
 * `/application` for a quote that already exists is not a user edit either:
 * the form only reads the stored quote, and the preview POST was the extra
 * `POST /v1/quotations` QA saw on that screen.
 */
export function shouldSkipBootCalcule(input: {
  pathname: string;
  quoteLoaded: boolean;
  writeMode: QuoteWriteMode;
}): boolean {
  if (input.writeMode !== 'editable') return true;
  if (!input.quoteLoaded) return false;
  return isApplicationPath(input.pathname);
}

/**
 * Resume may stay inside the router only for the client-portal board.
 * `/status` is mounted outside QuoteProvider, so that navigation unmounts it.
 * `/application/status` is not that board.
 */
export function isSpaStatusTarget(target: string): boolean {
  if (/^[a-z][a-z\d+.-]*:/i.test(target)) return false;
  const path = target.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  return path === '/status' || path.startsWith('/status/');
}
